import { addDays } from "date-fns"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks"
import { DocumentIcon } from "../../../ui/icons/documentIcon"
import { PdfDocumentIcon } from "../../../ui/icons/PdfDocumentIcon"
import { PrinterIcon } from "../../../ui/icons/PrinterIcon"
import { RoundedPlusIcon } from "../../../ui/icons/RoundedPlusIcon"
import { SquaredTickIcon } from "../../../ui/icons/SquaredTickIcon"
import { WordDocumentIcon } from "../../../ui/icons/WordDocumentIcon"
import { ButtonComponent } from "../../../ui/molecules/button/newButton"
import { SingleCheckbox } from "../../../ui/molecules/SingleCheckbox"
import { formatterDate, optionsFormatter } from "../../../utils"
import { MeasureStatusOptions, nonComplianceMap } from "../../../utils/measuresUtils"
import { NonComplianceMapping } from "../measuresOtherOdc/dataService/dto"
import { MeasureVersionEnum } from "./dataService/dto"
import { GetAllOldMeasure, resetGetAllOldCodexMeasureStatus, setOldCodexMeasureIdToUpdate } from "./edit/slice"
import { GetAllNewMeasure, GetNewMeasureById, NewMeasureTempDTO, OldMeasureTempDTO, resetGetAllAppliedResponseMeasure, resetGetAllNewMeasureStatus, resetGetNewMeasureByIdStatus, setGetAllNewMeasureCustomerId, setNewMeasureExpirationMeasure, setNewMeasureIdtoUpdate, setNewMeasureUpdateRequest, setSavedNewMeasures, setSavedOldMeasures } from "./slice"

enum MixedVersionEnum { OLD, NEW }

interface MixedMeasureDTO {
    version: MixedVersionEnum
    measureId: string
    measureDate: Date
    measureProtocolInfo: string
    notConformityCode: string
    notConformityInfo: string
    measureType: string
    measureTypeDetail: string
    expirationDate?: Date
    status: string
    closingProtocol?: string
    RAC?: string
}

export function CodexCustomerList() {
    const dispatch = useAppDispatch()
    const newMeasureState = useAppSelector(state => state.newMeasure)
    const oldCodexMeasure = useAppSelector(state => state.oldCodexMeasure)
    const navigate = useNavigate()
    const sessionState = useAppSelector(state => state.session)
    const [measures, setMeasures] = useState<MixedMeasureDTO[]>([])

    useEffect(() => {
        dispatch(setGetAllNewMeasureCustomerId(sessionState.selected.customerId))
    }, [sessionState.selected.customerId])

    useEffect(() => {
        dispatch(resetGetAllAppliedResponseMeasure())
    }, [])

    useEffect(() => {
        if (newMeasureState.getAllNewMeasureFilters?.customerId) {
            dispatch(GetAllNewMeasure(newMeasureState.getAllNewMeasureFilters))
        }
    }, [newMeasureState.getAllNewMeasureFilters])

    useEffect(() => {
        if (newMeasureState.getAllNewMeasureStatus === 'successfully' && newMeasureState.getAllNewMeasureResponse !== undefined) {
            dispatch(resetGetAllNewMeasureStatus())
            const savedMeasure = newMeasureState.getAllNewMeasureResponse.data.map(nm => ({
                id: nm.id,
                protocolInfo: nm.departureProtocolInfo,
                ncCode: { id: nm.newNonComplianceCodeDTO?.id, code: nm.newNonComplianceCodeDTO?.nonComplianceCode }
            } as NewMeasureTempDTO))
            dispatch(setSavedNewMeasures(savedMeasure))
            const data: MixedMeasureDTO[] = newMeasureState.getAllNewMeasureResponse.data.map(nm => ({
                version: MixedVersionEnum.NEW,
                measureId: nm.id,
                measureDate: nm.measureDate,
                measureProtocolInfo: nm.departureProtocolInfo,
                notConformityCode: nm.nonComplianceCodeVersion === MeasureVersionEnum.OLD ? nm.oldNonComplianceCode : nm.newNonComplianceCodeDTO?.nonComplianceCode,
                notConformityInfo: nm.nonComplianceCodeVersion === MeasureVersionEnum.OLD ? (nonComplianceMap.get(nm.oldNonComplianceCode) as NonComplianceMapping)?.nonComplianceDescription : nm.newNonComplianceCodeDTO?.description,
                measureType: nm.nonComplianceCodeVersion === MeasureVersionEnum.OLD ? (nonComplianceMap.get(nm.oldNonComplianceCode) as NonComplianceMapping)?.measureType : null,
                measureTypeDetail: nm.nonComplianceCodeVersion === MeasureVersionEnum.OLD ? (nonComplianceMap.get(nm.oldNonComplianceCode) as NonComplianceMapping)?.measureTypeDetail : null,
                closingProtocol: nm.statusProtocolInfo,
                status: optionsFormatter(nm.status, MeasureStatusOptions),
                rac: nm.actionPlaneProtocolInfo
            } as MixedMeasureDTO))
            setMeasures(prev => [...prev, ...data])
            dispatch(GetAllOldMeasure({ itemsPerPage: 0, page: 0, customerId: sessionState.selected.customerId!, isCodexReleased: true }))
        }
    }, [newMeasureState.getAllNewMeasureStatus])

    useEffect(() => {
        if (oldCodexMeasure.getAllOldCodexMeasureStatus === 'successfully' && oldCodexMeasure.getAllOldCodexMeasureResponse !== undefined) {
            dispatch(resetGetAllOldCodexMeasureStatus())
            const savedOldMeasure = oldCodexMeasure.getAllOldCodexMeasureResponse.data.map(om => ({
                id: om.id, protocolInfo: om.departureProtocolInfo, code: om.nonCompliance?.nonComplianceCode
            } as OldMeasureTempDTO))
            dispatch(setSavedOldMeasures(savedOldMeasure))
            const data: MixedMeasureDTO[] = oldCodexMeasure.getAllOldCodexMeasureResponse.data.map(om => ({
                version: MixedVersionEnum.OLD,
                measureId: om.id,
                measureDate: om.measureDate,
                measureProtocolInfo: om.departureProtocolInfo,
                notConformityCode: om.nonCompliance?.nonComplianceCode,
                notConformityInfo: om.nonCompliance?.nonComplianceDescription,
                measureType: (nonComplianceMap.get(om.nonCompliance?.nonComplianceCode) as NonComplianceMapping)?.measureType,
                measureTypeDetail: (nonComplianceMap.get(om.nonCompliance?.nonComplianceCode) as NonComplianceMapping)?.measureTypeDetail,
                closingProtocol: om.closingProtocolInfo,
                status: om.status,
                rac: om.racProtocolInfo
            } as MixedMeasureDTO))
            setMeasures(prev => [...prev, ...data])
        }
    }, [oldCodexMeasure.getAllOldCodexMeasureStatus])

    const onEditHandle = (m: MixedMeasureDTO) => {
        if (m.version === MixedVersionEnum.OLD) {
            dispatch(setOldCodexMeasureIdToUpdate(m.measureId))
            navigate('/addOldCodexMeasure')
        } else {
            dispatch(setNewMeasureIdtoUpdate(m.measureId))
            dispatch(GetNewMeasureById(m.measureId))
        }
    }

    useEffect(() => {
        if (newMeasureState.getNewMeasureByIdStatus === 'successfully' &&
            newMeasureState.getNewMeasureByIdResponse !== undefined
        ) {
            const mea = newMeasureState.getNewMeasureByIdResponse

            dispatch(setNewMeasureIdtoUpdate(mea.id))
            dispatch(setNewMeasureUpdateRequest({
                oldNonComplianceCode: mea.oldNonComplianceCode,
                newNonComplianceCodeId: mea.newNonComplianceCodeDTO?.id,
                nonComplianceNote: mea.nonComplianceNote,
                activities: mea.activities,
                productsCategories: mea.productsCategories,
                measureCorrectionVersion: mea.measureCorrectionVersion,
                measureCorrectionId: mea.measureCorrectionVersion === MeasureVersionEnum.NEW ? mea.newMeasureCorrectionId : mea.oldMeasureCorrectionId,
                oldNonComplianceCodeCorrection: mea.measureCorrectionVersion === MeasureVersionEnum.NEW ? mea.oldNonComplianceCodeCorrection : undefined,
                newNonComplianceCodeCorrectionId: mea.measureCorrectionVersion === MeasureVersionEnum.NEW ? mea.newNonComplianceCodeCorrectionDTO?.id : undefined,
                noCorrectionNote: mea.noCorrectionNote,
                nonComplianceLevel: mea.nonComplianceLevel,
                additionalNote: mea.additionalNote,
                controlType: mea.controlType,
                nonComplianceFindingDate: mea.nonComplianceFindingDate,
                nonComplianceFindingNote: mea.nonComplianceFindingNote,
                documentEntryProtocolId: mea.documentEntryProtocolId !== null ? mea.documentEntryProtocolId : undefined,
                documentDepartureProtocolId: mea.documentDepartureProtocolId !== null ? mea.documentDepartureProtocolId : undefined,
                documentIntegrationRequestId: mea.documentIntegrationRequestId,
                inspectionVisitIdTest: mea.inspectionVisitIdTest,
                findingFrom: mea.findingFrom,
                inspectorMeasureConfirmed: mea.inspectorMeasureConfirmed,
                laboratoryTestReportProtocolId: mea.laboratoryTestReportProtocolId !== null ? mea.laboratoryTestReportProtocolId : undefined,
                preventiveSuppressionM024IdTest: mea.preventiveSuppressionM024IdTest,
                m018IdTest: mea.m018IdTest,
                officerMeasureSignatureId: mea.officerMeasureSignatureId,
                departureChannel: mea.departureChannel,
                p0035IdTest: mea.p0035IdTest,
                measureRecipientsIds: mea.measureRecipientsDTO?.map(mr => mr.id),
                status: mea.status,
                statusProtocolId: mea.statusProtocolId !== null ? mea.statusProtocolId : undefined,
                actionPlaneProtocolId: mea.actionPlaneProtocolId !== null ? mea.actionPlaneProtocolId : undefined,
                closingNote: mea.closingNote,
                presentationDate: mea.presentationDate,
                presentationProtocolId: mea.presentationProtocolId !== null ? mea.presentationProtocolId : undefined,
                recourseDetail: mea.recourseDetail,
                appealReportDate: mea.appealReportDate,
                appealReportProtocolId: mea.appealReportProtocolId !== null ? mea.appealReportProtocolId : undefined,
                recourseResult: mea.recourseResult,
                recourseNote: mea.recourseNote,
                unitEmployed: mea.unitEmployed,
                recurrence: mea.recurrence,
                previousNonComplianceCode: mea.previousNonComplianceCode,
                recurrenceDetail: mea.recurrenceDetail,
                legislativeReferenceText: mea.legislativeReferenceText,
                specificAccessoryMeasures: mea.specificAccessoryMeasures,
                terminationTreatment: mea.terminationTreatment,
                otherAccessoryMeasures: mea.otherAccessoryMeasures,
                applicationMeasure: mea.applicationMeasure,
                verifyComplianceMeasure: mea.verifyComplianceMeasure,
                correctiveAction: mea.correctiveAction,
                measureDescription: mea.measureDescription,
                activeFrom: mea.activeFrom,
                expirationMeasure: mea.expirationMeasure,
                receiptProtocolId: mea.receiptProtocolId !== null ? mea.receiptProtocolId : undefined,
                closingProtocolId: mea.closingProtocolId !== null ? mea.closingProtocolId : undefined,
                racProtocolId: mea.racProtocolId !== null ? mea.racProtocolId : undefined,
                nextMeasureProtocolId: mea.nextMeasureProtocolId !== null ? mea.nextMeasureProtocolId : undefined,
                recourseDate: mea.recourseDate,
                resultProtocolId: mea.resultProtocolId !== null ? mea.resultProtocolId : undefined
            }))
            if(mea.activeFrom) {
                dispatch(setNewMeasureExpirationMeasure(addDays(mea.activeFrom, mea.terminationTreatment)))
            } else {
                dispatch(setNewMeasureExpirationMeasure(undefined))
            }
            dispatch(resetGetNewMeasureByIdStatus())
            navigate('/addNewCodexMeasureStep2')
        }
    }, [newMeasureState.getNewMeasureByIdStatus])

    return (
        <div className="p-6 flex flex-col gap-4">
            <div className="flex justify-end gap-4">
                <ButtonComponent
                    variant="outline"
                    size="md"
                    color="gray"
                    iconPosition="left"
                    icon={<DocumentIcon />}
                    label="Stampa provvedimenti selezionati"
                />
                <ButtonComponent
                    variant="solid"
                    disabled={sessionState.selected.customerId === undefined}
                    size="md"
                    color="orange"
                    iconPosition="left"
                    icon={<RoundedPlusIcon />}
                    label="Crea nuovo"
                    onClick={() => navigate('/addNewCodexMeasureStep1')}
                />
            </div>

            <div className="w-full overflow-x-auto rounded-xl border border-neutral-200 shadow-sm">
                <table className="w-full border-collapse text-text-md min-w-[1400px]">
                    <thead>
                        <tr className="bg-brandPrimary-600 text-white uppercase text-xs tracking-wide h-[52px]">
                            <th className="px-3 py-3 text-left font-semibold rounded-tl-xl w-8"></th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Data</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Protocollo Partenza</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Codice NC</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Descrizione NC</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Tipo Provvedimento</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Misure</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Data Scadenza</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Stato</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Prot. Chiusura</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">M024</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">Provvedimento</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">RAC</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">Seleziona</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">PDF Protocolli</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">Decaduto</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap rounded-tr-xl w-8"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {measures.length === 0 ? (
                            <tr>
                                <td colSpan={17} className="px-3 py-3 text-left text-neutral-500 bg-white">
                                    Nessun provvedimento trovato
                                </td>
                            </tr>
                        ) : (
                            measures.map((measure, index) => (
                                <tr
                                    key={index}
                                    className={`text-left border-b border-neutral-200 transition-colors duration-150 hover:bg-brandPrimary-50 ${index % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}`}
                                >
                                    <td className="px-3 py-2">
                                        <button onClick={() => onEditHandle(measure)} className="p-1 rounded hover:bg-neutral-100 transition-colors">
                                            <SquaredTickIcon />
                                        </button>
                                    </td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{formatterDate(measure.measureDate)}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{measure.measureProtocolInfo}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{measure.notConformityCode}</td>
                                    <td className="px-3 py-2 text-neutral-700">{measure.notConformityInfo}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{measure.measureType}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{measure.measureTypeDetail}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{formatterDate(measure.expirationDate)}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{measure.status}</td>
                                    <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{measure.closingProtocol}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2 justify-center">
                                            <button className="p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => { }}><PrinterIcon color="#5D9298" /></button>
                                            <button className="p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => { }}><PrinterIcon color="#0096D1" /></button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2 justify-center">
                                            <button className="p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => { }}><WordDocumentIcon /></button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2 justify-center">
                                            <button className="p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => { }}><WordDocumentIcon /></button>
                                            <button className="p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => { }}><PdfDocumentIcon /></button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex justify-center">
                                            <SingleCheckbox checked={false} setChecked={() => { }} label="" />
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2 justify-center">
                                            <button className="p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => { }}><PdfDocumentIcon /></button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-2 justify-center">
                                            <button className="p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => { }}><PrinterIcon color="#5D9298" /></button>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2"></td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}